import { AbstractControl } from '@angular/forms';
import { AuthFormFieldName, AuthFormName, AuthFormType } from '../enums/event/event-data.enum';
import { AuthEventRepo } from '../enums/event/event.enum';
import { BaseFormEventData, FieldError, FieldInteraction, FormFillStart, FormSubmit } from '../models/event-data.model';
import { FormErrors } from './form-errors.config';

type ErrorMessageFn = (c: AbstractControl) => string;

export interface FieldDeclaration {
  fieldName: AuthFormFieldName;
  errorFn: ErrorMessageFn;
}
export interface FieldTrackingConfig {
  event: FieldInteraction;
  errorFn: (c: AbstractControl) => FieldError;
}
export interface FormTrackingConfig {
  baseEvent: BaseFormEventData;
  formStartEvent: FormFillStart;
  formSubmitEvent: FormSubmit;
  fields: Record<string, FieldTrackingConfig>;
  eventRepo?: 'auth' | 'customerPortal';
}

class FormConfig implements FormTrackingConfig {
  public baseEvent: BaseFormEventData;
  public formStartEvent: FormFillStart;
  public formSubmitEvent: FormSubmit;
  public fields: Record<string, FieldTrackingConfig> = {};

  constructor(baseEventData: BaseFormEventData, fields: FieldDeclaration[]) {
    this.baseEvent = baseEventData;
    this.formStartEvent = { ...baseEventData };
    this.formSubmitEvent = { ...baseEventData };
    fields.forEach((x) => {
      const { fieldName: name, errorFn } = x;
      this.fields[name] = this.fieldConfig(name, errorFn);
    });
  }

  protected baseData(): BaseFormEventData {
    return { ...this.baseEvent };
  }

  protected fieldInteraction(fieldName: AuthFormFieldName): FieldInteraction {
    return { ...this.baseEvent, authFormFieldName: fieldName };
  }

  protected fieldError(fieldName: AuthFormFieldName, error: string): FieldError {
    const interaction = this.fieldInteraction(fieldName);
    return error && error != '' ? { ...interaction, authFormFieldErrorMessage: error } : null;
  }

  protected errorFn(fieldName: AuthFormFieldName, errorFn: ErrorMessageFn) {
    return (c: AbstractControl): FieldError => this.fieldError(fieldName, errorFn(c));
  }

  protected fieldConfig(fieldName: AuthFormFieldName, errorFn: ErrorMessageFn): FieldTrackingConfig {
    return {
      event: this.fieldInteraction(fieldName),
      errorFn: this.errorFn(fieldName, errorFn),
    };
  }
}

export const FormTracking: Record<AuthFormName, FormTrackingConfig> = {
  [AuthFormName.LOGIN]: new FormConfig({ authFormName: AuthFormName.LOGIN, authFormType: AuthFormType.LOGIN }, [
    { fieldName: AuthFormFieldName.USERNAME, errorFn: FormErrors.getUsernameInputError },
    { fieldName: AuthFormFieldName.PASSWORD, errorFn: FormErrors.getPasswordInputError },
  ]),
  [AuthFormName.REGISTER_FIND]: new FormConfig(
    { authFormName: AuthFormName.REGISTER_CREATE, authFormType: AuthFormType.LOGIN },
    [
      { fieldName: AuthFormFieldName.LAST_NAME, errorFn: FormErrors.getLastNameInputError },
      { fieldName: AuthFormFieldName.LAST_SIX_SSN, errorFn: FormErrors.getSsnInputError },
      { fieldName: AuthFormFieldName.DOB, errorFn: FormErrors.getDateOfBirthInputError },
    ]
  ),
  [AuthFormName.REGISTER_CREATE]: new FormConfig(
    { authFormName: AuthFormName.REGISTER_CREATE, authFormType: AuthFormType.LOGIN },
    [
      { fieldName: AuthFormFieldName.USERNAME, errorFn: FormErrors.getUsernameInputError },
      { fieldName: AuthFormFieldName.PASSWORD, errorFn: FormErrors.getPasswordCriteriaError },
      { fieldName: AuthFormFieldName.CONFIRM_PASSWORD, errorFn: FormErrors.getPasswordMismatchError },
      { fieldName: AuthFormFieldName.EMAIL_OPT_IN, errorFn: () => 'Please choose a verification method' },
      { fieldName: AuthFormFieldName.SMS_OPT_IN, errorFn: () => 'Please choose a verification method' },
      { fieldName: AuthFormFieldName.ACCEPT_TERMS, errorFn: () => 'Please acknowledge the terms and conditions' },
    ]
  ),
  [AuthFormName.REGISTER_VERIFY]: new FormConfig(
    { authFormName: AuthFormName.REGISTER_VERIFY, authFormType: AuthFormType.LOGIN },
    [{ fieldName: AuthFormFieldName.CONFIRMATION_CODE, errorFn: () => '' }]
  ),
  [AuthFormName.FIND_USERNAME]: new FormConfig(
    { authFormName: AuthFormName.FIND_USERNAME, authFormType: AuthFormType.LOGIN },
    [
      { fieldName: AuthFormFieldName.LAST_NAME, errorFn: FormErrors.getLastNameInputError },
      { fieldName: AuthFormFieldName.LAST_SIX_SSN, errorFn: FormErrors.getSsnInputError },
      { fieldName: AuthFormFieldName.DOB, errorFn: FormErrors.getDateOfBirthInputError },
    ]
  ),
  [AuthFormName.FORGOT_CONTACT_INFO]: new FormConfig(
    { authFormName: AuthFormName.FORGOT_CONTACT_INFO, authFormType: AuthFormType.LOGIN },
    [{ fieldName: AuthFormFieldName.CONTACT_INFO, errorFn: FormErrors.getContactMethodValueInputError }]
  ),
  [AuthFormName.FORGOT_PASSWORD]: new FormConfig(
    { authFormName: AuthFormName.FORGOT_PASSWORD, authFormType: AuthFormType.LOGIN },
    [
      { fieldName: AuthFormFieldName.USERNAME, errorFn: FormErrors.getUsernameInputError },
      { fieldName: AuthFormFieldName.RECAPTCHA, errorFn: () => '' },
    ]
  ),
  [AuthFormName.RESET_PASSWORD]: new FormConfig(
    { authFormName: AuthFormName.RESET_PASSWORD, authFormType: AuthFormType.LOGIN },
    [
      { fieldName: AuthFormFieldName.CONFIRMATION_CODE, errorFn: () => '' },
      { fieldName: AuthFormFieldName.PASSWORD, errorFn: FormErrors.getPasswordCriteriaError },
      { fieldName: AuthFormFieldName.CONFIRM_PASSWORD, errorFn: FormErrors.getPasswordMismatchError },
    ]
  ),
  [AuthFormName.PROFILE_VEHICLE_EDIT]: new FormConfig(
    {
      authFormName: AuthFormName.PROFILE_VEHICLE_EDIT,
      authFormType: AuthFormType.PROFILE,
      eventRepo: AuthEventRepo.Auth,
    },
    [
      { fieldName: AuthFormFieldName.PRIMARY_VEHICLE_OWNERSHIP_STATUS, errorFn: () => '' },
      { fieldName: AuthFormFieldName.PRIMARY_VEHICLE_MAKE, errorFn: () => '' },
      { fieldName: AuthFormFieldName.PRIMARY_VEHICLE_MODEL, errorFn: () => '' },
      { fieldName: AuthFormFieldName.PRIMARY_VEHICLE_YEAR, errorFn: () => '' },
      { fieldName: AuthFormFieldName.PRIMARY_VEHICLE_VIN, errorFn: () => '' },
      { fieldName: AuthFormFieldName.SECONDARY_VEHICLE_OWNERSHIP_STATUS, errorFn: () => '' },
      { fieldName: AuthFormFieldName.SECONDARY_VEHICLE_MAKE, errorFn: () => '' },
      { fieldName: AuthFormFieldName.SECONDARY_VEHICLE_MODEL, errorFn: () => '' },
      { fieldName: AuthFormFieldName.SECONDARY_VEHICLE_YEAR, errorFn: () => '' },
      { fieldName: AuthFormFieldName.SECONDARY_VEHICLE_VIN, errorFn: () => '' },
    ]
  ),
  [AuthFormName.PROFILE_RESIDENCE_EDIT]: new FormConfig(
    { authFormName: AuthFormName.PROFILE_RESIDENCE_EDIT, authFormType: AuthFormType.PROFILE },
    [
      { fieldName: AuthFormFieldName.RESIDENCE_OWNERSHIP_STATUS, errorFn: () => '' },
      { fieldName: AuthFormFieldName.RESIDENCE_MONTHLY_PAYMENT, errorFn: () => '' },
    ]
  ),
  [AuthFormName.PROFILE_INCOME_EDIT]: new FormConfig(
    {
      authFormName: AuthFormName.PROFILE_INCOME_EDIT,
      authFormType: AuthFormType.PROFILE,
    },
    [
      { fieldName: AuthFormFieldName.INDIVIDUAL_INCOME, errorFn: () => '' },
      { fieldName: AuthFormFieldName.HOUSEHOLD_INCOME, errorFn: () => '' },
      { fieldName: AuthFormFieldName.EMPLOYMENT_STATUS, errorFn: () => '' },
    ]
  ),
  [AuthFormName.CARD_SECOND_LEVEL_VERIFICATION]: new FormConfig(
    {
      authFormName: AuthFormName.CARD_SECOND_LEVEL_VERIFICATION,
      authFormType: AuthFormType.CARD_SECOND_LEVEL_VERIFICATION,
    },
    [{ fieldName: AuthFormFieldName.CARD_VERIFICATION_NUMBER, errorFn: () => '' }]
  ),
  [AuthFormName.PAPERLESS_ENROLLMENT]: new FormConfig(
    {
      authFormName: AuthFormName.PAPERLESS_ENROLLMENT,
      authFormType: AuthFormType.PAPERLESS_ENROLLMENT_INTERSTITIAL,
    },
    [{ fieldName: AuthFormFieldName.PAPERLESS_OPT_IN, errorFn: () => '' }]
  ),
  [AuthFormName.PAPERLESS_DECLINE]: new FormConfig(
    {
      authFormName: AuthFormName.PAPERLESS_DECLINE,
      authFormType: AuthFormType.PAPERLESS_ENROLLMENT_INTERSTITIAL,
    },
    [{ fieldName: AuthFormFieldName.PAPERLESS_OPT_IN, errorFn: () => '' }]
  ),
  [AuthFormName.PAPERLESS_MAYBE_LATER]: new FormConfig(
    {
      authFormName: AuthFormName.PAPERLESS_MAYBE_LATER,
      authFormType: AuthFormType.PAPERLESS_ENROLLMENT_INTERSTITIAL,
    },
    [{ fieldName: AuthFormFieldName.PAPERLESS_OPT_IN, errorFn: () => '' }]
  ),
  [AuthFormName.UPDATE_EMAIL_ADDRESS]: new FormConfig(
    {
      authFormName: AuthFormName.UPDATE_EMAIL_ADDRESS,
      authFormType: AuthFormType.PAPERLESS_ENROLLMENT_INTERSTITIAL,
    },
    [{ fieldName: AuthFormFieldName.EMAIL, errorFn: () => 'Please enter a valid email address' }]
  ),
};
